import { ConfigGroup, ConfigItem, ConfigValue, DataTypeControlConfigItem } from '@ecosystem/newron-client-lib';

import { CollectionInsight } from '../../shared/types';

export type MultiEditInsight<TInsightData> = Pick<CollectionInsight<TInsightData>, 'id' | 'type' | 'data' | 'dataType'>;

export enum KnownConfigItemType {
  ProductGroup = 'ProductGroup',
  TimeFrame = 'TimeFrame',
}

export interface ConfigItemDescriptor {
  /**
   * Shows as radio button label and Multi edit page title
   * */
  title: string;
  /**
   * Used as label of the entity
   */
  label?: string;
}

export interface MultiEditConfigItemType extends ConfigItemDescriptor {
  type: string;
}

export type AllowedType = KnownConfigItemType | MultiEditConfigItemType;
export type AllowedTypes = AllowedType[];

export type MultiEditConfigItem = Exclude<ConfigItem, ConfigGroup | DataTypeControlConfigItem>;

export interface ConfigItemInfo {
  /** The config item for the given type. value can be omitted */
  configItem: MultiEditConfigItem;
  /** Currently applied values for each provided insight */
  configValues: Record<string, ConfigValue>;
}

/**
 * MultiEditInfo is a set of callbacks that are used to get the config items for multi-editing insights.
 */
export interface MultiEditCallbacks {
  /**
   * Returns the allowed multi-editable config item types for the given insights.
   * This allows for either a known config item type or a generic type.
   * This should later be extended to allow for more types.
   *
   * @param insights Array of reduced insights owned by the calling proposition
   *
   * @returns The allowed types for the given insights. (e.g. `KnownConfigItemType.ProductGroup`, `{type: 'GenericType', label: 'Generic Type', title: 'Generic Type'}`)
   */
  getAllowedTypes: (insights: MultiEditInsight<unknown>[]) => AllowedTypes;

  /**
   * Should fetch options for given config item type.
   *
   * @param allowedType config item type (taken from the response of `getAllowedTypes`)
   * @param insights Array of reduced insights owned by the calling proposition
   *
   * @returns The config items for the given type and currently applied values for each provided insight
   */
  getConfigItemInfo: (
    allowedType: KnownConfigItemType | string,
    insights: MultiEditInsight<unknown>[]
  ) => Promise<ConfigItemInfo> | ConfigItemInfo;
}
